import { FC, useMemo, useReducer } from 'react';

import { SquarePen, Trash2 } from 'lucide-react';
import { addProp, isEmpty, pick } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { LevelFormDialog } from '../levelFormDialog/LevelFormDialog';

import css from './LevelsTableActionsColumn.module.css';

import { levelsService } from 'v2/api/levelsService';
import { rewardsService } from 'v2/api/rewardsService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { LevelForm, LevelModel } from 'v2/types/LevelModel';
import { handleHttpError, mapRewardsToFormRewards } from 'v2/utils';

interface LevelsTableActionsColumnProps {
  level: LevelModel;
  onRevalidate: VoidFunction;
  isLast?: boolean;
}

export const LevelsTableActionsColumn: FC<LevelsTableActionsColumnProps> = ({ level, onRevalidate, isLast }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data } = useSWR(
    isOpen && !isEmpty(level.levelRewards) && ['getRewardsByIds', level.levelRewards],
    rewardsService.getRewardsByIds
  );

  const { trigger: triggerUpdate, isMutating: isMutatingUpdate } = useSWRMutation(
    ['updateLevel', level],
    levelsService.updateLevel,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The level has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(['deleteLevel', level], levelsService.deleteLevel, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const initialValues = useMemo(() => {
    return addProp(
      pick(level, ['levelPoints', 'referralBonusPercent', 'dailyBonusPercent']),
      'rewards',
      isEmpty(level.levelRewards) ? [] : mapRewardsToFormRewards(data)
    );
  }, [data, level]);

  const handleSubmit = (values: LevelForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => resetForm());
  };

  return (
    <div className={css.levelsTableActionsColumn}>
      <Tooltip title="Edit" placement="top">
        <IconButton onClick={setOpen} color="warning">
          <SquarePen width={22} height={22} />
        </IconButton>
      </Tooltip>

      <Popconfirm
        message="Are you sure that you want to delete this level?"
        onConfirm={triggerDelete}
        disabled={!isLast}
      >
        <Tooltip title="Delete" placement="top">
          <IconButton color="error" disabled={!isLast}>
            <Trash2 width={22} height={22} />
          </IconButton>
        </Tooltip>
      </Popconfirm>

      <LevelFormDialog
        title="Edit Level"
        isOpen={isOpen}
        onClose={setOpen}
        initialValues={initialValues}
        isSubmitting={isMutatingUpdate}
        level={level.level}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
